import { createAsyncThunk } from '@/redux/createAsyncThunk';
import {
    getEmailLists,
    postSubscribeToEmailList,
    postUnsubscribeFromEmailList,
} from '@/redux/modules/account/notifications/emailList/emailList.api';
import {
    getEmailList,
    shouldFetchEmailLists,
} from '@/redux/modules/account/notifications/emailList/emailList.selectors';
import {
    EmailList,
    UNSUBSCRIBE_TO_ALL_EMAIL_LISTS,
} from '@/redux/modules/account/notifications/emailList/emailList.types';
import { getAuthToken } from '@/redux/modules/account/user/user.selectors';
import { getDeployment } from '@/redux/modules/config';
import { fetchSmsAlertsIfNeeded } from '@/redux/modules/smsAlerts';

/**
 * @deprecated If using toolkit, use unsubscribeToEmailList.fulfilled instead
 */
export const UNSUBSCRIBE_SUCCESS = 'la/user/emailList/UNSUBSCRIBE_SUCCESS';

export const fetchEmailLists = createAsyncThunk<EmailList[], void>(
    'la/ui/emailList/fetchEmailLists',
    async (_, { getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        const { data } = await getEmailLists({
            authToken,
            deployment,
        });
        return data;
    }
);

export const fetchEmailListsIfNeeded = createAsyncThunk(
    'la/ui/emailList/fetchEmailListsIfNeeded',
    async (_, { dispatch, getState }) => {
        const state = getState();
        if (shouldFetchEmailLists(state)) {
            await dispatch(fetchEmailLists());
        }
    }
);

type ToggleEmailListSubscriptionParams = {
    code: EmailList['code'];
    force?: boolean;
};

export const subscribeToEmailList = createAsyncThunk<boolean, EmailList['code']>(
    'la/ui/emailList/subscribeToEmailList',
    async (code, { getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const { data } = await postSubscribeToEmailList({
            authToken,
            code,
            deployment,
        });
        return data;
    }
);

export const unsubscribeToEmailList = createAsyncThunk<boolean, ToggleEmailListSubscriptionParams>(
    'la/ui/emailList/unsubscribeToEmailList',
    async ({ code, force }, { dispatch, getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const result = await postUnsubscribeFromEmailList({
            authToken,
            code,
            deployment,
        });
        dispatch({
            meta: { code },
            type: UNSUBSCRIBE_SUCCESS,
        });
        if (force) {
            await dispatch(fetchSmsAlertsIfNeeded(authToken, force));
        }
        return result.data;
    }
);

export const toggleEmailListSubscription = createAsyncThunk<void, ToggleEmailListSubscriptionParams>(
    'la/ui/emailList/toggleEmailListSubscription',
    async ({ code, force }, { dispatch, getState }) => {
        const state = getState();
        const list = getEmailList(state, code);
        if (list.subscribed || code === UNSUBSCRIBE_TO_ALL_EMAIL_LISTS || force) {
            await dispatch(unsubscribeToEmailList({ code, force }));
        } else {
            await dispatch(subscribeToEmailList(code));
        }
    }
);
